import * as React from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton, TextField, TextFieldVariants } from '@mui/material';
import HelpIcon from '@material-ui/icons/Help';
import { renderHelpText } from './renderHelpText';
import { useState } from 'react';

export type InputType = 'text' | 'password' | 'email' | 'number' | 'date' | 'month' | 'datetime-local' | 'time' | 'tel' | 'url' | 'search' | 'file' | 'textarea';

export interface TextInputFieldProps {
    value?: string|number,
    id?: string,
    checked?: boolean,
    name?: string,
    type?: InputType,
    disabled?: boolean, // readOnly and greyed out
    readOnly?: boolean,
    valid?: boolean,
    invalid?: boolean,
    required?: boolean,
    maxLength?: number,
    variant?: TextFieldVariants | 'nobox',
    placeholder?: string,
    label?: React.ReactNode,
    helpText?: string,
    description?: React.ReactNode,
    style?: React.CSSProperties,
    inputStyle?: React.CSSProperties,
    endAdornment?: React.ReactNode,
    onChange?: (value: string, file?: File) => void,
    onKeyUp?: React.KeyboardEventHandler<HTMLInputElement>,
    onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>,
    className?: string,
    rows?: number,
    autoFocus?: boolean,

    pattern?: string,
    step?: string|number,
    min?: string|number,
    max?: string|number,
    accept?: string,
}

export const TextInputField = (props: TextInputFieldProps) => {
    const {
        value,
        id,
        name,
        type,
        disabled,
        readOnly,
        placeholder,
        label,
        helpText,
        description,
        invalid,
        required,
        variant = 'outlined',
        maxLength,
        style,
        inputStyle,
        endAdornment,
        onChange,
        onKeyUp,
        onBlur,
        className,
        rows,
        pattern,
        step,
        min,
        max,
        accept,
        autoFocus,
    } = props;

    const [showHelp, setShowHelp] = useState(false);
    
    const mapValue = (v?: string|number) => {
        switch (type) {
            case 'date': return typeof v === 'string' ? v.substring(0, 10) : ''
            case 'datetime-local': return typeof v === 'string' ? (v.length === 10 ? `${v}T00:00` : v.substring(0, 16)) : ''
            default: return v !== undefined ? value : ''
        }
    }

    const rowsVal = typeof rows === 'number' ? rows 
        : (type === 'textarea' ? Math.max(3, (typeof value === 'string' ? value : '').split('\n').length) : undefined)

    // For file type, use native input
    if (type === 'file') {
        return (
            <input
                id={id ? `${id}-input` : undefined}
                style={style}
                className={className}
                type="file"
                name={name}
                disabled={disabled}
                accept={accept}
                onChange={e => onChange && onChange(e.target.value, e.target.files?.item(0) || undefined)}
                onKeyUp={onKeyUp}
            />
        );
    }

    return (<>
        <TextField
            id={id ? `${id}-input` : undefined}
            className={className}
            style={style}
            type={type || 'text'}
            name={name}
            value={mapValue(value)}
            disabled={disabled}
            placeholder={placeholder}
            autoFocus={autoFocus}
            label={helpText
                ? <span>{label} <IconButton size="small" onClick={() => setShowHelp(true)}><HelpIcon/></IconButton></span>
                : label}
            helperText={description}
            error={invalid}
            required={required}
            slotProps={{
                input: {
                    ...(endAdornment ? { endAdornment } : {}),
                    inputProps: {
                        style: {
                            ...type === 'textarea' && { resize: 'vertical' },
                            ...inputStyle,
                        },
                        maxLength,
                        pattern,
                        step,
                        min,
                        max,
                        readOnly: disabled || readOnly,
                    },
                },
                inputLabel: {
                    shrink: true,
                },
            }}
            multiline={type === 'textarea'}
            rows={rowsVal}
            onChange={e => onChange && onChange(e.target.value)}
            onKeyUp={onKeyUp}
            onBlur={onBlur}
            sx={variant === 'nobox' ? {
                "& .MuiInput-underline:before": { borderBottom: "none" },
                // "& .MuiInput-underline:after": { borderBottom: "none" },
                // "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottom: "none" }
            } : undefined}
            variant={variant === 'nobox' ? 'standard' : variant}
            size="small"
            fullWidth
        />

        {(helpText?.length &&
            <Dialog open={showHelp} onClose={() => setShowHelp(false)}>
                <DialogTitle>{label}</DialogTitle>
                <DialogContent>{renderHelpText(helpText)}</DialogContent>
            </Dialog>
        ) || undefined}
    </>);
}
